import { type ReactNode, useEffect, useRef } from 'react';
import { useFocusTrap } from '../../lib/use-focus-trap.js';

export type DialogVariant = 'modal' | 'slideover' | 'sheet';

interface DialogProps {
  open: boolean;
  onClose: () => void;
  /** Accessible name. Ignored when `labelledBy` is provided. */
  label?: string;
  /** id of a heading inside the dialog (preferred over `label`). */
  labelledBy?: string;
  /** modal = centered card · slideover = right panel · sheet = bottom sheet. */
  variant?: DialogVariant;
  /** Extra classes for the panel. */
  className?: string;
  /** data-testid for the panel; the scrim gets `${testId}-backdrop`. */
  testId?: string;
  /** Close when the scrim is clicked (default true). */
  closeOnScrimClick?: boolean;
  children: ReactNode;
}

/**
 * The ONE overlay primitive (R3). Every dialog / slide-over / bottom-sheet in the
 * redesign mounts through this so they share a single contract:
 *   - one scrim token (var(--scrim)) and one z-scale (overlay = 100; toasts = 110)
 *   - role="dialog" + aria-modal + an accessible name (label / labelledBy)
 *   - Esc closes; scrim click closes (opt-out via closeOnScrimClick)
 *   - focus trapped inside while open, restored to the opener on close
 * Replaces the four ad-hoc z-index conventions (z-40/50/60/1000) and the
 * focus-trap-less modals the a11y review flagged.
 */
const VARIANT_SCRIM: Record<DialogVariant, string> = {
  modal: 'flex items-center justify-center p-4',
  slideover: 'flex',
  sheet: 'flex items-end',
};
const VARIANT_PANEL: Record<DialogVariant, string> = {
  modal: 'w-full max-w-md',
  slideover: 'ml-auto h-full w-full max-w-sm',
  sheet: 'mt-auto w-full',
};

export function Dialog({
  open,
  onClose,
  label,
  labelledBy,
  variant = 'modal',
  className,
  testId,
  closeOnScrimClick = true,
  children,
}: DialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  useFocusTrap(panelRef, open);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const nameProps = labelledBy
    ? { 'aria-labelledby': labelledBy }
    : label
      ? { 'aria-label': label }
      : {};

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: the scrim is a click-to-dismiss layer; keyboard close is handled by the Esc listener above and the dialog's own controls.
    <div
      data-testid={testId ? `${testId}-backdrop` : undefined}
      onMouseDown={
        closeOnScrimClick
          ? (e) => {
              if (e.target === e.currentTarget) onClose();
            }
          : undefined
      }
      className={`fixed inset-0 z-[100] ${VARIANT_SCRIM[variant]}`}
      style={{ background: 'var(--scrim)' }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        {...nameProps}
        data-testid={testId}
        tabIndex={-1}
        className={`relative outline-none ${VARIANT_PANEL[variant]} ${className ?? ''}`}
      >
        {children}
      </div>
    </div>
  );
}
